import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import "./rules.css";
import "../../styles/adminPanels.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_RIGHT_ANSWER_POINTS = 100;
const MAX_CATEGORIES = 6;
const MAX_CATEGORY_LEN = 15;
const CATEGORY_NAME_RE = /^[a-zA-Z0-9\s]+$/;
const MAX_THANK_YOU_LEN = 300;
const MAX_TAB_NAME_LEN = 20;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

function parseStoredBool(raw, fallback = true) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [textColor, setTextColor] = useState("#000000");
  const [boxColor, setBoxColor] = useState("#1e293b");
  const [questionBoxColor, setQuestionBoxColor] = useState("#1e293b");
  const [optionBoxColor, setOptionBoxColor] = useState("#1e293b");
  const [boxTextColor, setBoxTextColor] = useState("#ffffff");
  const [iconColor, setIconColor] = useState("#ffd700");

  const [categoriesStatus, setCategoriesStatus] = useState(true);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [categoriesSaving, setCategoriesSaving] = useState(false);
  const [thankYouText, setThankYouText] = useState("");
  const [tabName, setTabName] = useState("");

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    const r = ensureArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);

    setTextColor(toColorInputValue(data.textcolor));
    setBoxColor(toColorInputValue(data.box_color ?? data.boxcolor ?? "#1e293b"));
    // Question/option box colors fall back to Box Color so themes saved
    // before this split still show the color picker matching what's live.
    setQuestionBoxColor(toColorInputValue(data.question_box_color ?? data.box_color ?? data.boxcolor ?? "#1e293b"));
    setOptionBoxColor(toColorInputValue(data.option_box_color ?? data.box_color ?? data.boxcolor ?? "#1e293b"));
    setBoxTextColor(toColorInputValue(data.box_text_color ?? "#ffffff"));
    setIconColor(toColorInputValue(data.icon_color ?? "#ffd700"));

    setCategoriesStatus(parseStoredBool(data.categories_status, true));
    setCategories(ensureArray(data.categories));
    setThankYouText(String(data.custom_text_thank_you_page ?? ""));
    setTabName(String(data.tab_name ?? ""));
  }, [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > MAX_RULE_LEN) {
      Swal.fire("Limit", `Each rule can be at most ${MAX_RULE_LEN} characters.`, "warning");
      val = val.slice(0, MAX_RULE_LEN);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmedRules = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmedRules.length === 0) return { ok: false, msg: "At least one rule is required." };
    for (const r of trimmedRules) {
      if (r.length > MAX_RULE_LEN) return { ok: false, msg: "Each rule must be 200 characters or less." };
    }
    if (thankYouText.length > MAX_THANK_YOU_LEN) {
      return { ok: false, msg: `Thank you text must be ${MAX_THANK_YOU_LEN} characters or less.` };
    }
    if (tabName.length > MAX_TAB_NAME_LEN) {
      return { ok: false, msg: `Tab name must be ${MAX_TAB_NAME_LEN} characters or less.` };
    }

    return {
      ok: true,
      payload: {
        rules: trimmedRules,
        textcolor: textColor,
        box_color: boxColor,
        question_box_color: questionBoxColor,
        option_box_color: optionBoxColor,
        box_text_color: boxTextColor,
        icon_color: iconColor,
        // Preserve existing fields that aren't edited on this page (points/colors
        // live on the Question Panel settings, landing copy lives elsewhere)
        landing_page_title: String(data?.landing_page_title ?? "").trim(),
        // May be saved empty — the thank-you page simply shows nothing below the table then.
        custom_text_thank_you_page: thankYouText.trim(),
        // May be saved empty — the browser tab then falls back to the default title.
        tab_name: tabName.trim(),
        points: clampInt(data?.points, 1, MAX_RIGHT_ANSWER_POINTS, 1),
        wrong_points: clampInt(data?.wrong_points, 0, MAX_RIGHT_ANSWER_POINTS, 0),
        total_question: clampInt(data?.total_question, 1, 100, 10),
        button_color: data?.button_color ?? "#667eea",
        button_Textcolor: data?.button_Textcolor ?? data?.button_text_color ?? "#ffffff",
        text_bg_color: data?.text_bg_color ?? "#ffffff",
        icon_bg_color: data?.icon_bg_color ?? data?.text_bg_color ?? "#ffffff",
        use_timeout: parseStoredBool(data?.use_timeout, true),
        question_timeout_minutes: clampInt(data?.question_timeout_minutes, 0, 59, 1),
        question_timeout_seconds: clampInt(data?.question_timeout_seconds, 0, 59, 30),
        main: ensureArray(data?.main),
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    const name = newCategory.trim().toUpperCase().replace(/\s+/g, " ");
    if (!name) return;
    if (categories.length >= MAX_CATEGORIES) {
      Swal.fire("Limit", `Up to ${MAX_CATEGORIES} categories.`, "warning");
      return;
    }
    if (name.length > MAX_CATEGORY_LEN) {
      Swal.fire("Limit", `Category name must be ${MAX_CATEGORY_LEN} characters or less.`, "warning");
      return;
    }
    if (!CATEGORY_NAME_RE.test(name)) {
      Swal.fire("Invalid name", "Special characters are not allowed in category names.", "warning");
      return;
    }
    if (categories.some((c) => c.toUpperCase() === name)) {
      Swal.fire("Duplicate", "That category already exists.", "warning");
      return;
    }
    setCategories((prev) => [...prev, name]);
    setNewCategory("");
  };

  const handleRemoveCategory = (idx) => {
    setCategories((prev) => prev.filter((_, i) => i !== idx));
  };

  // The "Add Categories" switch saves itself immediately — it must not depend on
  // the "Save Categories" button below, which is only rendered while the toggle
  // is on and therefore vanishes the instant the admin turns it off.
  const handleToggleCategoriesStatus = async (checked) => {
    const previous = categoriesStatus;
    setCategoriesStatus(checked);
    setCategoriesSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { categories_status: checked },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
    } catch (e) {
      setCategoriesStatus(previous);
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setCategoriesSaving(false);
    }
  };

  const handleSaveCategories = async () => {
    if (categories.length === 0) {
      Swal.fire("Required", "At least one category is required.", "warning");
      return;
    }
    setCategoriesSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { categories, categories_status: categoriesStatus },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Categories updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save categories", "error");
    } finally {
      setCategoriesSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="rules-php-layout">
        <div className="rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Colors &amp; Rules</h4>

           

              <div className="rules-php-color-grid">
                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-text-color" className="rules-php-marks-label">
                    Text Color
                  </label>
                  <input
                    id="rules-text-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-box-color" className="rules-php-marks-label">
                    Box Color (Buttons)
                  </label>
                  <input
                    id="rules-box-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={boxColor}
                    onChange={(e) => setBoxColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-question-box-color" className="rules-php-marks-label">
                    Question Box Color
                  </label>
                  <input
                    id="rules-question-box-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={questionBoxColor}
                    onChange={(e) => setQuestionBoxColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-option-box-color" className="rules-php-marks-label">
                    Option Box Color
                  </label>
                  <input
                    id="rules-option-box-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={optionBoxColor}
                    onChange={(e) => setOptionBoxColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-box-text-color" className="rules-php-marks-label">
                    Box Text Color
                  </label>
                  <input
                    id="rules-box-text-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={boxTextColor}
                    onChange={(e) => setBoxTextColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row rules-php-color-cell">
                  <label htmlFor="rules-icon-color" className="rules-php-marks-label">
                    Icon Color
                  </label>
                  <input
                    id="rules-icon-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={iconColor}
                    onChange={(e) => setIconColor(e.target.value)}
                  />
                </div>
              </div>

              <h6 className="rules-php-subheading">Browser Tab Name</h6>
              <input
                type="text"
                className="themeupdate-input"
                placeholder="Shown as the browser tab title. Can be left empty."
                maxLength={MAX_TAB_NAME_LEN}
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
              />
              <p className="themeupdate-muted">
                <code>
                  * Shown as the browser tab title on every page. Maximum {MAX_TAB_NAME_LEN} characters. Can be left empty.
                </code>
              </p>

              <h6 className="rules-php-subheading">Custom Text Thank You Page</h6>
              <textarea
                className="themeupdate-input rules-php-textarea"
                placeholder="Shown below the leaderboard table. Can be left empty."
                maxLength={MAX_THANK_YOU_LEN}
                value={thankYouText}
                onChange={(e) => setThankYouText(e.target.value)}
              />
              <p className="themeupdate-muted">
                <code>
                  * Shown below the leaderboard table. Maximum {MAX_THANK_YOU_LEN} characters. Can be left empty.
                </code>
              </p>

              <h6 className="rules-php-subheading">Custom Rules</h6>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Rules"
                      maxLength={MAX_RULE_LEN}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="themeupdate-muted">
                  <code>
                    * Up to {MAX_RULES} rules, at least 1 required, {MAX_RULE_LEN} characters each
                  </code>
                </p>
              </div>

              <div style={{ marginTop: 16, textAlign: "center" }}>
                <button type="button" className="save-and-continue" onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save Content"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Question Panel</h4>

              <div className="theme-question-panel-box">
                <p className="themeupdate-muted" style={{ marginBottom: 12 }}>
                  Manage quiz questions for this theme.
                </p>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={() => navigate("/admin/questionadmin")}
                >
                  <i className="fa-solid fa-edit" /> Edit Questions
                </button>
              </div>

              <div className="theme-panel-toggle">
                <Switch
                  checked={categoriesStatus}
                  onChange={handleToggleCategoriesStatus}
                  disabled={categoriesSaving}
                  onColor="#4caf50"
                  height={22}
                  width={44}
                />
                <label className="rules-php-marks-label" style={{ margin: 0 }}>
                  Add Categories
                </label>
              </div>

              {categoriesStatus && (
                <div className="theme-categories-block">
                  <div className="themeupdate-rules-repeater">
                    {categories.map((category, i) => (
                      <div key={`${category}-${i}`} className="rules-php-repeater-row">
                        <input type="text" className="themeupdate-input" value={category} readOnly />
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-danger"
                          onClick={() => handleRemoveCategory(i)}
                        >
                          <i className="fa-solid fa-times" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="rules-php-repeater-row" style={{ marginTop: 8 }}>
                    <input
                      type="text"
                      className="themeupdate-input"
                      placeholder="New category name"
                      maxLength={MAX_CATEGORY_LEN}
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                    />
                    <button type="button" className="themeupdate-btn themeupdate-btn-primary" onClick={handleAddCategory}>
                      <i className="fa-solid fa-plus" /> Add new category
                    </button>
                  </div>

                  <p className="themeupdate-muted" style={{ marginTop: 8 }}>
                    <code>
                      * Up to {MAX_CATEGORIES} categories. Max {MAX_CATEGORY_LEN} characters each.
                    </code>
                  </p>

                  <div style={{ marginTop: 16, textAlign: "center" }}>
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-success"
                      onClick={handleSaveCategories}
                      disabled={categoriesSaving}
                    >
                      {categoriesSaving ? "Saving..." : "Save Categories"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
